import { datadogRum } from '@datadog/browser-rum';
import { useAuth } from '@state/auth';
import { useEffect, useRef } from 'react';
import { isManagementPath } from '@/lib/postLoginPath';
import { setV2OptedOut } from '@/lib/v2OptInState';
import pkg from '../../package.json';

const RETRACED_ENV = import.meta.env.VITE_RETRACED_ENV;

/** v2 appends this when the user leaves it deliberately. */
const OPT_OUT_PARAM = 'optOut';

const OPT_OUT_ACTION = 'frontend_v2_auto_redirect_opted_out';

/**
 * Records a deliberate return from v2 and cleans the marker out of the URL.
 *
 * This is all that remains of the old auto-forward. Nothing sends a user to v2
 * on its own any more — each dashboard's login lands on that dashboard's own
 * management area, and crossing over is a deliberate act via the navbar banner.
 * The marker still arrives, because v2's "back to the old dashboard" control
 * sends it, and it is still worth reading: it answers the launch prompt, so
 * someone who has been to v2 and come back is not asked about it again.
 */
export function useV2OptOutParam() {
  const me = useAuth(state => state.me);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get(OPT_OUT_PARAM) !== '1') return;

    handledRef.current = true;
    setV2OptedOut();
    // Client pages only — see `isManagementPath`. Staff returning from v2's
    // management area to this one is not a signal about customer adoption.
    if (!isManagementPath(window.location.pathname)) {
      datadogRum.addAction(OPT_OUT_ACTION, {
        Version: pkg.version,
        env: RETRACED_ENV,
        userId: me?.id,
      });
    }
    params.delete(OPT_OUT_PARAM);
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    );
  }, [me?.id]);
}
